/**
 * UsageAnalyticsClient — API client for Usage Analytics data.
 *
 * Uses the same AgentPlatformClient singleton from SkillClient.ts
 * to call GetUsageMetrics and GetInsightsReport.
 */

import { getSkillClient } from '../../client/SkillClient';
import {
  GetUsageMetricsCommand,
  GetInsightsReportCommand,
  ListInsightsReportsCommand,
  GetTaskMetricsCommand,
} from '@amzn/cmhk-agent-platform-service-client';

// ── Types ────────────────────────────────────────────────────────────────────

export interface DailySession {
  date: string;
  count: number;
}

export interface ChannelDistribution {
  channel: string;
  count: number;
}

export interface TopTool {
  toolName: string;
  count: number;
}

export interface TopUser {
  userId: string;
  sessions: number;
  inputTokens: number;
  outputTokens: number;
  channel: string;
  lastActive: string;
}

export interface LatencyBucket {
  bucket: string;
  count: number;
}

/** Week-over-week (period-over-period) comparison for latency. */
export interface LatencyWoW {
  /** Previous period avg in ms. */
  previousAvgMs: number;
  /** Previous period P95 in ms. */
  previousP95Ms: number;
  /** Avg change percentage (positive = increased / worse, negative = improved). */
  avgChangePercent: number;
  /** P95 change percentage. */
  p95ChangePercent: number;
}

/** Latency stats for a specific category (scheduled or interactive). */
export interface CategoryLatency {
  avgMs: number;
  p50Ms: number;
  p95Ms: number;
  sampleCount: number;
  /** Period-over-period comparison for this category. */
  wow: LatencyWoW | null;
}

export interface ResponseLatency {
  avgMs: number;
  p50Ms: number;
  p95Ms: number;
  sampleCount: number;
  buckets: LatencyBucket[];
  /** Latency for scheduled task executions. */
  scheduledLatency: CategoryLatency | null;
  /** Latency for interactive (non-scheduled) executions. */
  interactiveLatency: CategoryLatency | null;
  /** Overall period-over-period comparison. */
  wow: LatencyWoW | null;
}

export interface UsageMetrics {
  activeUsers: number;
  sessions: number;
  messages: number;
  inputTokens: number;
  outputTokens: number;
  toolCalls: number;
  dailySessions: DailySession[];
  channelDistribution: ChannelDistribution[];
  topTools: TopTool[];
  topUsers: TopUser[];
  responseLatency: ResponseLatency | null;
}

export interface IntentRanking {
  rank: number;
  intent: string;
  sessions: number;
  description: string;
}

export interface FailureRootCause {
  name: string;
  sessions: number;
  recommendation: string;
}

export interface FailureSubCategory {
  name: string;
  sessions: number;
  rootCauses: FailureRootCause[];
}

export interface FailureCategory {
  name: string;
  sessions: number;
  totalSessions: number;
  rootCauseCount: number;
  subCategories: FailureSubCategory[];
}

export interface ExecutionPattern {
  pattern: string;
  sessions: number;
  description: string;
}

export interface InsightsReport {
  reportDate: string;
  sessionsAnalyzed: number;
  intentRanking: IntentRanking[];
  failureCategories: FailureCategory[];
  executionPatterns: ExecutionPattern[];
}

// ── API Functions ────────────────────────────────────────────────────────────

export interface InsightsReportSummary {
  reportDate: string;
  batchId: string;
  sessionsAnalyzed: number;
  periodStart: string;
  periodEnd: string;
  reportType: string;
  status: string;
  createdAt: string;
}

export async function fetchInsightsReportList(agentId: string): Promise<InsightsReportSummary[]> {
  const client = getSkillClient();
  const command = new ListInsightsReportsCommand({ agentId, limit: 20 });
  const resp = await client.send(command);
  return (resp.reports ?? []).map((r) => ({
    reportDate: r.reportDate ?? '',
    batchId: r.batchId ?? '',
    sessionsAnalyzed: r.sessionsAnalyzed ?? 0,
    periodStart: r.periodStart ?? '',
    periodEnd: r.periodEnd ?? '',
    reportType: r.reportType ?? 'weekly',
    status: r.status ?? '',
    createdAt: r.createdAt ?? '',
  }));
}

export async function fetchUsageMetrics(
  agentId: string,
  period: string,
  startDate?: string,
  endDate?: string,
): Promise<UsageMetrics> {
  const client = getSkillClient();
  const command = new GetUsageMetricsCommand({ agentId, period, ...(startDate ? { startDate } : {}), ...(endDate ? { endDate } : {}) });
  const resp = await client.send(command);

  return {
    activeUsers: resp.summary?.totalUsers ?? 0,
    sessions: resp.summary?.totalSessions ?? 0,
    messages: resp.summary?.totalMessages ?? 0,
    inputTokens: resp.summary?.totalInputTokens ?? 0,
    outputTokens: resp.summary?.totalOutputTokens ?? 0,
    toolCalls: resp.summary?.totalToolCalls ?? 0,
    dailySessions: (resp.dailyTrend ?? []).map((d) => ({
      date: d.date ?? '',
      count: d.sessions ?? 0,
    })),
    channelDistribution: (resp.channelDistribution ?? []).map((c) => ({
      channel: c.source ?? 'unknown',
      count: c.sessionCount ?? 0,
    })),
    topTools: (resp.topTools ?? []).map((t) => ({
      toolName: t.toolName ?? '',
      count: t.callCount ?? 0,
    })),
    topUsers: (resp.topUsers ?? []).map((u) => ({
      userId: u.userId ?? '',
      sessions: u.sessionCount ?? 0,
      inputTokens: u.inputTokens ?? 0,
      outputTokens: u.outputTokens ?? 0,
      channel: u.primaryChannel ?? 'unknown',
      lastActive: u.lastActive ?? '',
    })),
    responseLatency: resp.responseLatency ? (() => {
      const prev = resp.responseLatencyPrevious;
      const bySource = resp.latencyBySource;
      const calcWoW = (cur: { avgMs?: number; p95Ms?: number }, prevStats: { avgMs?: number; p95Ms?: number } | undefined | null): LatencyWoW | null => {
        if (!prevStats || !prevStats.avgMs) return null;
        const prevAvg = prevStats.avgMs ?? 0;
        const prevP95 = prevStats.p95Ms ?? 0;
        const curAvg = cur.avgMs ?? 0;
        const curP95 = cur.p95Ms ?? 0;
        return {
          previousAvgMs: prevAvg,
          previousP95Ms: prevP95,
          avgChangePercent: prevAvg > 0 ? ((curAvg - prevAvg) / prevAvg) * 100 : 0,
          p95ChangePercent: prevP95 > 0 ? ((curP95 - prevP95) / prevP95) * 100 : 0,
        };
      };
      const mapCategory = (stats: any, prevStats: any): CategoryLatency | null => {
        if (!stats) return null;
        return {
          avgMs: stats.avgMs ?? 0,
          p50Ms: stats.p50Ms ?? 0,
          p95Ms: stats.p95Ms ?? 0,
          sampleCount: stats.sampleCount ?? 0,
          wow: calcWoW(stats, prevStats),
        };
      };
      return {
        avgMs: resp.responseLatency.avgMs ?? 0,
        p50Ms: resp.responseLatency.p50Ms ?? 0,
        p95Ms: resp.responseLatency.p95Ms ?? 0,
        sampleCount: resp.responseLatency.sampleCount ?? 0,
        buckets: (resp.responseLatency.buckets ?? []).map((b) => ({
          bucket: b.bucket ?? '',
          count: b.count ?? 0,
        })),
        scheduledLatency: mapCategory(bySource?.scheduled, bySource?.scheduledPrevious),
        interactiveLatency: mapCategory(bySource?.interactive, bySource?.interactivePrevious),
        wow: calcWoW(resp.responseLatency, prev),
      };
    })() : null,
  };
}

export async function fetchInsightsReport(
  agentId: string,
  reportDate?: string,
): Promise<InsightsReport> {
  const client = getSkillClient();
  const command = new GetInsightsReportCommand({
    agentId,
    ...(reportDate ? { reportDate } : {}),
  });
  const resp = await client.send(command);

  return {
    reportDate: resp.reportDate ?? '',
    sessionsAnalyzed: resp.sessionsAnalyzed ?? 0,
    intentRanking: (resp.userIntents ?? []).map((intent, idx) => ({
      rank: idx + 1,
      intent: intent.name ?? '',
      sessions: intent.sessionCount ?? 0,
      description: intent.description ?? '',
    })),
    failureCategories: (resp.failures ?? []).map((cat) => {
      const subs = (cat.subCategories ?? []).map((sub) => ({
        name: sub.name ?? '',
        sessions: sub.sessionCount ?? 0,
        rootCauses: (sub.rootCauses ?? []).map((rc) => ({
          name: rc.name ?? '',
          sessions: sub.sessionCount ?? 0,
          recommendation: rc.recommendation ?? '',
        })),
      }));
      return {
        name: cat.name ?? '',
        sessions: cat.sessionCount ?? 0,
        totalSessions: resp.sessionsAnalyzed ?? 0,
        rootCauseCount: subs.reduce((sum, s) => sum + s.rootCauses.length, 0),
        subCategories: subs,
      };
    }),
    executionPatterns: (resp.executionPatterns ?? []).map((p) => ({
      pattern: p.name ?? '',
      sessions: p.sessionCount ?? 0,
      description: p.description ?? '',
    })),
  };
}

// ── Task Metrics Types ───────────────────────────────────────────────────────

export interface TaskMetrics {
  summary: {
    activeTasks: number;
    totalExecutions: number;
    successCount: number;
    failedCount: number;
    successRate: number;
  };
  dailyExecutions: { date: string; success: number; failed: number }[];
  typeDistribution: { taskType: string; count: number }[];
  durationDistribution: { bucket: string; count: number }[];
  tasks: {
    taskId: string;
    name: string;
    owner: string;
    creator: string;
    taskType: string;
    schedule: string;
    lastStatus: string;
    lastExecutionTime: string;
    executionCount: number;
    failureCount: number;
    createdAt: string;
  }[];
  topFailed: {
    taskId: string;
    name: string;
    owner: string;
    failures: number;
    lastError: string;
    lastFailureTime: string;
  }[];
}

export async function fetchTaskMetrics(
  period: string,
  startDate?: string,
  endDate?: string,
  agent?: string,
): Promise<TaskMetrics> {
  const client = getSkillClient();
  const command = new GetTaskMetricsCommand({
    period,
    ...(startDate ? { startDate } : {}),
    ...(endDate ? { endDate } : {}),
    ...(agent ? { agent } : {}),
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const resp: any = await client.send(command);

  return {
    summary: {
      activeTasks: resp.summary?.activeTasks ?? 0,
      totalExecutions: resp.summary?.totalExecutions ?? 0,
      successCount: resp.summary?.successCount ?? 0,
      failedCount: resp.summary?.failedCount ?? 0,
      successRate: resp.summary?.successRate ?? 100,
    },
    dailyExecutions: (resp.dailyExecutions ?? []).map((d: any) => ({
      date: d.date ?? '',
      success: d.success ?? 0,
      failed: d.failed ?? 0,
    })),
    typeDistribution: (resp.typeDistribution ?? []).map((t: any) => ({
      taskType: t.taskType ?? '',
      count: t.count ?? 0,
    })),
    durationDistribution: (resp.durationDistribution ?? []).map((d: any) => ({
      bucket: d.bucket ?? '',
      count: d.count ?? 0,
    })),
    tasks: (resp.tasks ?? []).map((t: any) => ({
      taskId: t.taskId ?? '',
      name: t.name ?? '',
      owner: t.owner ?? '',
      creator: t.creator ?? '',
      taskType: t.taskType ?? '',
      schedule: t.schedule ?? '',
      lastStatus: t.lastStatus ?? 'NEVER',
      lastExecutionTime: t.lastExecutionTime ?? '',
      executionCount: t.executionCount ?? 0,
      failureCount: t.failureCount ?? 0,
      createdAt: t.createdAt ?? '',
    })),
    topFailed: (resp.topFailed ?? []).map((f: any) => ({
      taskId: f.taskId ?? '',
      name: f.name ?? '',
      owner: f.owner ?? '',
      failures: f.failures ?? 0,
      lastError: f.lastError ?? '',
      lastFailureTime: f.lastFailureTime ?? '',
    })),
  };
}